/**
 * PrintHubContext — runs the Master Print Station loop GLOBALLY (mounted
 * once at the app root) so it keeps printing queued labels no matter which
 * page the operator is on. The station computer can start the Hub, then
 * navigate to Temperature / Label Printer / anywhere in the same tab and
 * the Hub keeps draining the queue in the background.
 *
 * The /print-hub page is now just a control + monitor surface for this
 * shared state. Runs for admins only. The `enabled` flag is persisted so
 * the Hub resumes automatically after a reload / wake (paired with
 * TecorContext's auto-reconnect).
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api, { withSite } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTecor } from "@/contexts/TecorContext";

const Ctx = createContext(null);
const HUB_KEY = "haccp_hub_enabled";

export function PrintHubProvider({ children }) {
  const { activeSite } = useSite();
  const { user } = useAuth();
  const { connected, printB64 } = useTecor();

  const [hubEnabled, setHubEnabledState] = useState(() => {
    try { return localStorage.getItem(HUB_KEY) === "1"; } catch { return false; }
  });
  const [hubWorking, setHubWorking] = useState(false);
  const [hubRecent, setHubRecent] = useState([]);
  const [hubPending, setHubPending] = useState(0);
  const [hubLastError, setHubLastError] = useState("");

  const isAdmin = user?.role === "admin";
  const siteId = activeSite?.id;

  const setHubEnabled = useCallback((v) => {
    setHubEnabledState(v);
    try { localStorage.setItem(HUB_KEY, v ? "1" : "0"); } catch { /* ignore */ }
  }, []);

  const processingRef = useRef(false);
  const enabledRef = useRef(hubEnabled);
  const connectedRef = useRef(connected);
  enabledRef.current = hubEnabled;
  connectedRef.current = connected;

  const loadRecent = useCallback(async () => {
    if (!siteId) return;
    try {
      const [{ data: rec }, { data: pend }] = await Promise.all([
        api.get("/print-queue/recent", { params: { site_id: siteId, limit: 10 } }),
        api.get("/print-queue/pending", { params: { site_id: siteId, limit: 200 } }),
      ]);
      setHubRecent(rec || []);
      setHubPending((pend || []).length);
    } catch { /* transient */ }
  }, [siteId]);

  const processOne = useCallback(async () => {
    const { data } = await api.post("/print-queue/claim", {}, withSite(siteId));
    const job = data?.job;
    if (!job) return false;
    try {
      const r = await api.post("/labels/render-tspl", job.payload, withSite(job.site_id || siteId));
      if (!r.data?.bytes_b64) throw new Error("Render failed (Training Mode blocks printing).");
      await printB64(r.data.bytes_b64);
      await api.patch(`/print-queue/${job.id}`,
        { status: "completed", printed_at: new Date().toISOString() }, withSite(siteId));
      setHubLastError("");
    } catch (e) {
      const msg = e?.message || "Print error";
      await api.patch(`/print-queue/${job.id}`,
        { status: "failed", error: String(msg).slice(0, 200) }, withSite(siteId)).catch(() => {});
      throw e;
    }
    return true;
  }, [siteId, printB64]);

  const drain = useCallback(async () => {
    if (processingRef.current) return;
    if (!enabledRef.current || !connectedRef.current || !siteId) return;
    processingRef.current = true;
    setHubWorking(true);
    try {
      let n = 0;
      while (enabledRef.current && connectedRef.current) {
        // eslint-disable-next-line no-await-in-loop
        const did = await processOne();
        if (!did) break;
        n += 1;
        if (n >= 50) break; // safety cap per cycle
      }
    } catch (e) {
      setHubLastError(e?.message || "Print error");
      setHubEnabled(false); // pause on a jam so we don't spin
    } finally {
      processingRef.current = false;
      setHubWorking(false);
      loadRecent();
    }
  }, [siteId, processOne, loadRecent, setHubEnabled]);

  // Global 2s poll — independent of the current route.
  useEffect(() => {
    if (!isAdmin || !siteId) return undefined;
    loadRecent();
    const t = setInterval(() => {
      loadRecent();
      if (enabledRef.current && connectedRef.current) drain();
    }, 2000);
    return () => clearInterval(t);
  }, [isAdmin, siteId, drain, loadRecent]);

  const value = {
    hubEnabled, setHubEnabled, hubWorking, hubRecent, hubPending, hubLastError,
    hubActive: hubEnabled && connected, refreshHub: loadRecent, drainNow: drain, isAdmin,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePrintHub() {
  return (
    useContext(Ctx) || {
      hubEnabled: false, setHubEnabled: () => {}, hubWorking: false, hubRecent: [],
      hubPending: 0, hubLastError: "", hubActive: false,
      refreshHub: async () => {}, drainNow: async () => {}, isAdmin: false,
    }
  );
}
